import { HTTP_INTERCEPTORS, HttpInterceptor } from '@angular/common/http';
import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpLoadingInterceptor } from '@assetaccountant/core/http-loading.interceptor';
import { AuthenticationService } from '@assetaccountant/core/services/authentication.service';
import { CheckForUpdateService } from '@assetaccountant/core/services/check-for-update.service';
import { FeatureFlagLoaderService } from '@assetaccountant/core/services/feature-flag-loader.service';
import { FeatureFlagService } from '@assetaccountant/core/services/feature-flag.service';
import { ActiveComponentService } from '@assetaccountant/core/services/title.service';
import { TrackingService } from '@assetaccountant/core/services/tracking.service';

import { ObserveResizeEvent } from '@assetaccountant/core/directives/observe-resize.directive';
import { ClientSwitchService } from '@assetaccountant/core/services/client-switch.service';
import { LayoutService } from '@assetaccountant/core/services/layout.service';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';
import { NgbDropdownConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  readonly environmentName = environment.name;
  readonly navigationApiUrl = environment.navigationApiUrl;

  isDemo$ = this.layoutService.isDemo;

  scrollbarWidth = 0;
  universalClientId: string | null = null;
  chromeless = false;

  constructor(
    private authService: AuthenticationService,
    private featureService: FeatureFlagService,
    private featureFlagLoader: FeatureFlagLoaderService,
    private router: Router,
    public activeComponentService: ActiveComponentService,
    private layoutService: LayoutService,
    private checkForUpdateService: CheckForUpdateService,
    private trackingService: TrackingService,
    private appNavigation: AppNavigationService,
    private clientSwitch: ClientSwitchService,
    @Inject(HTTP_INTERCEPTORS) interceptors: HttpInterceptor[],
    ngbTooltipConfig: NgbTooltipConfig,
    ngbDropdownConfig: NgbDropdownConfig,
  ) {
    ngbTooltipConfig.container = 'body';
    ngbDropdownConfig.container = 'body';

    featureService.detectFeatures();

    // Session initialisation (loading the current user) happens in ngOnInit / initializeSession().

    interceptors
      .find((i): i is HttpLoadingInterceptor => i instanceof HttpLoadingInterceptor)
      ?.listenToNavigation(router.events);
  }

  ngOnInit() {
    this.checkForUpdateService.init();
    this.featureService.outputFeatures();
    this.activeComponentService.init();

    this.initializeSession();
    this.setupUcidTracking();
  }

  private initializeSession() {
    // Migration: the SPA no longer runs a client-side OIDC flow (Duende is being decommissioned;
    // users authenticate via the LeaseQuery platform "APIAuth" cookie). Load the current user from
    // the API, then start routing. A 401 is handled by the HTTP interceptor (redirect to the platform
    // login); we start navigation regardless so the app renders in all cases.
    this.authService
      .loadCurrentUser()
      .then(loaded => loaded && this.trackingService.identifyUser())
      .finally(() => this.router.initialNavigation());
  }

  private setupUcidTracking() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const activeOrg = this.appNavigation.getActiveOrganisation();
        if (activeOrg) {
          this.universalClientId = activeOrg.universalClientId;
          this.featureFlagLoader.loadForOrganisation(activeOrg.organisationId);
        }
        this.chromeless = this.isChromelessRoute();
      }
    });
  }

  /** Routes with `data: { chromeless: true }` render without the nav, header and banners. */
  private isChromelessRoute() {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data['chromeless'] === true;
  }

  /** The nav bar's client selector. It bubbles and is composed, so it reaches the document. */
  @HostListener('document:client-selected', ['$event'])
  async onClientSelected(event: CustomEvent<{ clientId?: string }>) {
    await this.clientSwitch.switchToClient(event.detail?.clientId);
  }

  ngOnDestroy() {
    this.activeComponentService.destroy();
  }

  onMainResize($event: ObserveResizeEvent<'clientWidth' | 'offsetWidth'>) {
    const newScrollbarWidth = $event.offsetWidth - $event.clientWidth;
    if (this.scrollbarWidth !== newScrollbarWidth) {
      this.scrollbarWidth = newScrollbarWidth;
    }
  }
}
